import { createFileRoute } from '@tanstack/react-router'
import { FolderSearch } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { Input } from '@/shared/ui/input'
import { SelectNative } from '@/shared/ui/select-native'
import { PageHeader } from '@/widgets/app-shell/page-header'
import { FiltroCampo } from '@/widgets/data-table/table-toolbar'

export const Route = createFileRoute('/_authed/mais-opcoes/historico-de-atividades')({
  component: Page,
})

const isoLocal = (d: Date) => {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${String(d.getFullYear())}-${m}-${day}`
}
const daysAgo = (n: number) => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d
}

/**
 * MOLDURA do histórico de atividades.
 *
 * Não há back-end de auditoria/log — então nada é inventado: os filtros são
 * renderizados (estado local, exploráveis) mas "Filtrar" fica desabilitado com o
 * selo "Em breve", porque não há o que consultar. A lista é um empty state
 * honesto. O banner de topo (nav-config `coming-soon`) explica a causa.
 */
function Page() {
  const { t } = useTranslation(['moreOptions', 'common'])

  const [descricao, setDescricao] = useState('')
  const [atividade, setAtividade] = useState('')
  const [contato, setContato] = useState('')
  const [de, setDe] = useState(() => isoLocal(daysAgo(30)))
  const [ate, setAte] = useState(() => isoLocal(new Date()))

  const limpar = () => {
    setDescricao('')
    setAtividade('')
    setContato('')
    setDe('')
    setAte('')
  }

  return (
    <>
      <PageHeader title={t('activity.title')} />
      <div className="flex flex-col gap-6 p-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-56">
            <FiltroCampo id="ha-descricao" label={t('activity.filters.description')}>
              <Input
                id="ha-descricao"
                value={descricao}
                onChange={(e) => {
                  setDescricao(e.target.value)
                }}
              />
            </FiltroCampo>
          </div>

          <div className="w-full sm:w-48">
            <FiltroCampo id="ha-atividade" label={t('activity.filters.activity')}>
              <SelectNative
                id="ha-atividade"
                value={atividade}
                onChange={(e) => {
                  setAtividade(e.target.value)
                }}
              >
                <option value="">{t('activity.filters.allActivities')}</option>
              </SelectNative>
            </FiltroCampo>
          </div>

          <div className="w-full sm:w-56">
            <FiltroCampo id="ha-contato" label={t('activity.filters.contact')}>
              <SelectNative
                id="ha-contato"
                value={contato}
                onChange={(e) => {
                  setContato(e.target.value)
                }}
              >
                <option value="">{t('activity.filters.contactPlaceholder')}</option>
              </SelectNative>
            </FiltroCampo>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-ink-muted text-xs font-medium">
              {t('activity.filters.period')}
            </span>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                aria-label={`${t('activity.filters.period')} — ${t('common:toolbar.from')}`}
                className="w-40"
                max={de !== '' && ate !== '' ? ate : undefined}
                value={de}
                onChange={(e) => {
                  setDe(e.target.value)
                }}
              />
              <Input
                type="date"
                aria-label={`${t('activity.filters.period')} — ${t('common:toolbar.to')}`}
                className="w-40"
                min={de !== '' && ate !== '' ? de : undefined}
                value={ate}
                onChange={(e) => {
                  setAte(e.target.value)
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Sem endpoint de log: filtrar não tem o que consultar → desabilitado. */}
            <Button variant="outline" size="md" disabled title={t('common:comingSoon.body')}>
              {t('activity.filters.apply')}
            </Button>
            <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
            <Button variant="link" size="md" onClick={limpar}>
              {t('activity.filters.clear')}
            </Button>
          </div>
        </div>

        <EmptyState
          icon={FolderSearch}
          title={t('activity.empty.title')}
          description={t('activity.empty.description')}
        />
      </div>
    </>
  )
}
